import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["name", "id"];
import { useEffect, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch, generatePath, useLocation, useParams } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import ApiError from '../components/Errors/ApiError';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import UsageTracker from '../utils/UsageTracker';
import { VideoContentWithInteractiveEducation } from '../features/interactiveEducation/components/VideoContentWithInteractiveEducation';
import { useUserIndustry } from '../features/interactiveEducation/hooks/useUserIndustry';
import { buildInteractiveContentTrackingProps } from '../features/interactiveEducation/utils/contentUtils';
import { LEGACY_SHORT_CONTENT_URL_FRAGMENT, SHORT_CONTENT_URL_FRAGMENT } from '../constants/Navigation';
import { SCREENS } from '../constants/Tracking';
import { LearningContentPageLayout } from '../features/learningContent';
import { NextPlaylistContentOverview, PlaylistComplete, PlaylistIncompleteItems } from '../features/playlistContent';
import { RecommendationsForLearningContent, useSetLastClickedRecommendationItemFromUrl } from '../features/recommendations';
import { ShortContentDetails, ShortContentHeader, ShortContentInstructors, ShortContentPlaylistNavigation, ShortContentVideo, ShortsSurvey, useShortContentParams } from '../features/shortContent';
import { usePlaylistProgress } from '../features/userProgress/userProgressQueries';
import { NavPageRoute } from '../features/navigation/components/NavPageRoute';
import { usePageTitle } from '../hooks/usePageTitle';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { useFullPlaylist } from '../features/savedItems/savedItemsQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ShortContentBody = ({
  sectionViewProps,
  short,
  playlistId
}) => {
  const {
    id: shortId,
    language
  } = short;
  const {
    isTablet,
    isNotLaptop
  } = useMediaQuery();
  const videoId = short !== null && short !== void 0 && short.reference ? Number(short.reference) : undefined;
  const contentPortalId = short === null || short === void 0 ? void 0 : short.hubId;
  const {
    label: industry
  } = useUserIndustry();
  const interactiveContentTrackingProps = useMemo(() => buildInteractiveContentTrackingProps({
    content: short,
    contentType: 'SHORT_FORM_CONTENT',
    contentId: short.id,
    professorId: short === null || short === void 0 ? void 0 : short.professors.map(professor => professor.id),
    industry,
    visibility: short === null || short === void 0 ? void 0 : short.visibility
  }), [short, industry]);
  const {
    aiAccessControls,
    loading: aiAccessControlsLoading
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const interactiveEducationEnabled = isNotLaptop && aiEnabled;
  useEffect(() => {
    if (short && !aiAccessControlsLoading) {
      const feature = interactiveEducationEnabled ? ['AI-QUIZ-ME', 'AI-SUMMARIZATION', 'AI-ADAPT-TO-INDUSTRY'] : [];
      const restContentTrackingProps = _objectWithoutPropertiesLoose(interactiveContentTrackingProps, _excluded);
      UsageTracker.trackStandalone('Pageview', Object.assign({
        screen: SCREENS.SHORT,
        contentId: shortId,
        contentName: short.name,
        feature
      }, restContentTrackingProps));
      if (window.scrollTo) {
        window.scrollTo(0, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveEducationEnabled]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "SHORT_CONTENT_LOADED",
    children: [/*#__PURE__*/_jsx(ShortsSurvey, {}), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "600",
      children: [/*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(VideoContentWithInteractiveEducation, {
          contentPortalId: contentPortalId,
          contentType: "SHORT_FORM_CONTENT",
          interactiveContentTrackingProps: interactiveContentTrackingProps,
          primaryContentId: shortId,
          videoId: videoId,
          videoTitle: short === null || short === void 0 ? void 0 : short.name,
          children: ({
            displayInteractiveEducation,
            handleDisplayInteractiveEducation
          }) => /*#__PURE__*/_jsx(ShortContentVideo, {
            additionalTracking: interactiveContentTrackingProps,
            displayInteractiveEducation: displayInteractiveEducation,
            handleDisplayInteractiveEducation: handleDisplayInteractiveEducation,
            interactiveEducationEnabled: interactiveEducationEnabled,
            playlistId: playlistId,
            short: short,
            viewProps: Object.assign({}, sectionViewProps, {
              paddingInline: undefined,
              paddingBlock: undefined
            })
          })
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        viewProps: sectionViewProps,
        children: /*#__PURE__*/_jsx(Flex, {
          children: /*#__PURE__*/_jsx(ShortContentDetails, {
            short: short
          })
        })
      }), short.professors.length > 0 && /*#__PURE__*/_jsx(LearningPageMainSection, {
        viewProps: sectionViewProps,
        children: /*#__PURE__*/_jsx(ShortContentInstructors, {
          short: short
        })
      }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
        appearsIn: "short-content-recommendations",
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.shortContent.videoRecommendations.description"
        }),
        direction: isTablet ? 'column' : 'row',
        id: shortId,
        language: language,
        size: isTablet ? 'small' : 'large',
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.shortContent.videoRecommendations.title"
        }),
        type: "SHORT_FORM_CONTENT"
      })]
    })]
  });
};
export const ShortPlaylistCompletionView = ({
  playlistNavigation,
  shortId
}) => {
  switch (playlistNavigation) {
    case 'next-in-playlist':
      return /*#__PURE__*/_jsx(NextPlaylistContentOverview, {
        navMarkerName: "SHORT_NEXT_IN_PLAYLIST_LOADED",
        shortId: shortId
      });
    case 'playlist-incomplete':
      return /*#__PURE__*/_jsx(PlaylistIncompleteItems, {
        navMarkerName: "SHORT_PLAYLIST_INCOMPLETE_LOADED"
      });
    default:
      return /*#__PURE__*/_jsx(PlaylistComplete, {
        navMarkerName: "SHORT_PLAYLIST_COMPLETE_LOADED"
      });
  }
};
export const ShortContent = () => {
  const {
    shortId,
    playlistId,
    playlistNavigation
  } = useShortContentParams();
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: [{
      id: shortId,
      type: 'SHORT_FORM_CONTENT'
    }],
    skip: !shortId
  });
  const short = cardModels === null || cardModels === void 0 ? void 0 : cardModels[0];
  const shortsError = cardModelsError || (!(cardModels !== null && cardModels !== void 0 && cardModels.length) ? {
    status: 404
  } : null);
  const {
    playlist
  } = useFullPlaylist(playlistId, !playlistId);
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId, !playlistId);
  const sectionViewProps = {
    backgroundColor: 'fill-surface-default',
    borderColor: 'border-core-subtle',
    borderWidth: '100',
    borderRadius: '300',
    borderStyle: 'solid',
    paddingInline: playlistId ? '400' : '1000',
    paddingBlock: playlistId ? '400' : '1000',
    overflow: 'hidden'
  };
  useSetLastClickedRecommendationItemFromUrl({
    id: shortId ? Number(shortId) : undefined,
    type: 'SHORT_FORM_CONTENT'
  });
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.shortVideoName',
    options: {
      videoName: short === null || short === void 0 ? void 0 : short.name
    },
    skip: !(short !== null && short !== void 0 && short.name)
  });
  if (!shortId) {
    return null;
  }
  if (cardModelsLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 200
    });
  }
  if (shortsError) {
    return /*#__PURE__*/_jsx(ApiError, {
      contentType: "SHORT_FORM_CONTENT",
      error: shortsError,
      marker404: "SHORT_CONTENT_API_404",
      markerDefault: "SHORT_CONTENT_API_ERROR"
    });
  }
  if (!short) return null;
  return /*#__PURE__*/_jsx(LearningContentPageLayout, {
    header: /*#__PURE__*/_jsx(View, {
      paddingBlock: "600",
      children: /*#__PURE__*/_jsx(ShortContentHeader, {
        playlist: playlist || undefined,
        playlistProgress: playlistProgress || undefined,
        short: short
      })
    }),
    navigation: playlistId && /*#__PURE__*/_jsx(ShortContentPlaylistNavigation, {
      short: short
    }),
    children: playlistNavigation ? /*#__PURE__*/_jsx(LearningPageMainSection, {
      viewProps: Object.assign({}, sectionViewProps, {
        paddingBlock: undefined,
        paddingInline: undefined
      }),
      children: /*#__PURE__*/_jsx(ShortPlaylistCompletionView, {
        playlistNavigation: playlistNavigation,
        shortId: shortId
      })
    }) : /*#__PURE__*/_jsx(ShortContentBody, {
      playlistId: playlist === null || playlist === void 0 ? void 0 : playlist.id,
      sectionViewProps: sectionViewProps,
      short: short
    })
  });
};
const RedirectToLanguagelessShort = () => {
  const location = useLocation();
  const {
    shortId,
    playlistNavigation
  } = useParams();
  let newPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
    shortId
  });
  if (playlistNavigation) newPath += `/${playlistNavigation}`;
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: newPath
    })
  });
};
export const ShortContentRoute = () => {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(NavPageRoute, {
        component: ShortContent,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.shortVideo",
        path: SHORT_CONTENT_URL_FRAGMENT,
        shouldRenderCenteredContent: false,
        shouldRenderCompactSearchBar: true
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToLanguagelessShort,
        exact: true,
        path: LEGACY_SHORT_CONTENT_URL_FRAGMENT
      })]
    })
  });
};
export default ShortContentRoute;